import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { invoiceApi } from './invoiceApi'


export const fetchBatches = createAsyncThunk(
    'invoice/fetchBatches',
    async (params = {}) => {
        const response = await invoiceApi.getBatches(params)
        return response.data
    }
)

export const createBatch = createAsyncThunk(
    'invoice/createBatch',
    async (batchData) => {
        const response = await invoiceApi.createBatch(batchData)
        return response.data
    }
)

export const fetchBatchDetail = createAsyncThunk(
    'invoice/fetchBatchDetail',
    async (batchId) => {
        const response = await invoiceApi.getBatchDetail(batchId)
        return response.data
    }
)

export const confirmBatch = createAsyncThunk(
    'invoice/confirmBatch',
    async ({ batchId, selectedContracts }) => {
        const response = await invoiceApi.confirmBatch(batchId, selectedContracts)
        return { ...response.data, batchId }
    }
)

export const fetchBatchDrafts = createAsyncThunk(
    'invoice/fetchBatchDrafts',
    async (batchId) => {
        const response = await invoiceApi.getBatchDrafts(batchId)
        return response.data
    }
)

export const sendToContifico = createAsyncThunk(
    'invoice/sendToContifico',
    async (draftId) => {
        const response = await invoiceApi.sendToContifico(draftId)
        return { ...response.data, draftId }
    }
)

export const previewBatch = createAsyncThunk(
    'invoice/previewBatch',
    async (batchData) => {
        const response = await invoiceApi.previewBatch(batchData)
        return response.data
    }
)

export const saveBatch = createAsyncThunk(
    'invoice/saveBatch',
    async (batchData) => {
        const response = await invoiceApi.saveBatch(batchData)
        return response.data
    }
)

export const saveBatchMigrate = createAsyncThunk(
    'invoice/saveBatchMigrate',
    async (batchData) => {
        const response = await invoiceApi.saveBatchMigrate(batchData)
        return response.data
    }
)

export const updateAndMigrateBatch = createAsyncThunk(
    'invoice/updateAndMigrateBatch',
    async (batchId) => {
        const response = await invoiceApi.updateAndMigrateBatch(batchId)
        return { ...response.data, batchId }
    }
)

export const deleteBatch = createAsyncThunk(
    'invoice/deleteBatch',
    async (batchId) => {
        await invoiceApi.deleteBatch(batchId)
        return { id: batchId }
    }
)


const invoiceSlice = createSlice({
    name: 'invoice',
    initialState: {
        // Nuevo estado para batches
        batches: [],
        currentBatch: null,
        batchDrafts: [],

        // Estado mantenido para compatibilidad temporal
        draftInvoices: [],
        historyInvoices: [],
        selectedInvoices: [],

        // Filtros
        filters: {
            month: '',
            client: '',
            status: ''
        },
        loading: false,
        error: null,
        currentBatchView: 'preview' // 'preview', 'drafts'
    },
    reducers: {
        setCurrentBatchView: (state, action) => {
            state.currentBatchView = action.payload
        },
        setCurrentBatch: (state, action) => {
            state.currentBatch = action.payload
            if (action.payload === null) {
                state.error = null
            }
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload }
        },
        toggleContractSelection: (state, action) => {
            const contractId = action.payload
            if (!state.currentBatch?.preview_details) return

            const contractIndex = state.currentBatch.preview_details.findIndex(
                item => item.contract_id === contractId
            )

            if (contractIndex > -1) {
                state.currentBatch.preview_details[contractIndex].is_selected =
                    !state.currentBatch.preview_details[contractIndex].is_selected
            }
        },
        selectAllContracts: (state, action) => {
            const { selected } = action.payload
            if (!state.currentBatch?.preview_details) return

            state.currentBatch.preview_details.forEach(item => {
                item.is_selected = selected && item.is_valid
            })
        },
        clearSelection: (state) => {
            if (state.currentBatch?.preview_details) {
                state.currentBatch.preview_details.forEach(item => {
                    item.is_selected = false
                })
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Batches
            .addCase(fetchBatches.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchBatches.fulfilled, (state, action) => {
                state.loading = false
                state.batches = Array.isArray(action.payload) ? action.payload : action.payload.results || []
            })
            .addCase(fetchBatches.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            // Create Batch
            .addCase(createBatch.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(createBatch.fulfilled, (state, action) => {
                state.loading = false
                state.currentBatch = action.payload
                // Agregar el nuevo batch a la lista
                if (!state.batches.find(b => b.id === action.payload.id)) {
                    state.batches.unshift(action.payload)
                }
            })
            .addCase(createBatch.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            // Create fetchBatchDetail
            .addCase(fetchBatchDetail.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchBatchDetail.fulfilled, (state, action) => {
                state.loading = false
                state.currentBatch = action.payload
                state.previewBatch = action.payload
                // Agregar el nuevo batch a la lista
                if (!state.batches.find(b => b.id === action.payload.id)) {
                    state.batches.unshift(action.payload)
                }
            })
            .addCase(fetchBatchDetail.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(updateAndMigrateBatch.pending, (state) => {
                state.loading = true
            })
            .addCase(updateAndMigrateBatch.fulfilled, (state, action) => {
                state.loading = false
                const { batchId } = action.payload
                const batch = state.batches.find(b => b.id === batchId)
                if (batch) batch.status = 'sent'
                if (state.currentBatch?.id === batchId) {
                    state.currentBatch.status = 'sent'
                }
            })
            .addCase(updateAndMigrateBatch.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            // Confirm Batch
            .addCase(confirmBatch.fulfilled, (state, action) => {
                const { batchId } = action.payload
                // Actualizar estado del batch
                const batch = state.batches.find(b => b.id === batchId)
                if (batch) {
                    batch.status = 'confirmed'
                }
                if (state.currentBatch?.id === batchId) {
                    state.currentBatch.status = 'confirmed'
                    state.currentBatchView = 'drafts'
                }
            })
            // Fetch Batch Drafts
            .addCase(fetchBatchDrafts.fulfilled, (state, action) => {
                state.batchDrafts = action.payload
            })
            // Send to Contifico
            .addCase(sendToContifico.fulfilled, (state, action) => {
                const { draftId } = action.payload
                const draft = state.batchDrafts.find(d => d.id === draftId)
                if (draft) {
                    draft.status = 'sent'
                }
            })
            .addCase(previewBatch.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(previewBatch.fulfilled, (state, action) => {
                state.loading = false
                state.currentBatch = { ...action.payload, is_preview: true }
            })
            .addCase(previewBatch.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })

            // Save Batch (persistir en DB)
            .addCase(saveBatch.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(saveBatch.fulfilled, (state, action) => {
                state.loading = false
                state.currentBatch = { ...action.payload, is_preview: false }

            })
            .addCase(saveBatch.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            // Save Batch (persistir en DB) y migrar a Contífico
            .addCase(saveBatchMigrate.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(saveBatchMigrate.fulfilled, (state, action) => {
                state.loading = false
                state.currentBatch = { ...action.payload, is_preview: false }

            })
            .addCase(saveBatchMigrate.rejected, (state, action) => {
                state.loading = false
                state.error = action.error.message
            })
            .addCase(deleteBatch.fulfilled, (state, action) => {
                state.batches = state.batches.filter(b => b.id !== action.payload.id)
                if (state.currentBatch?.id === action.payload.id) {
                    state.currentBatch = null
                }
            })
    }
})

export const {
    setCurrentBatchView,
    setCurrentBatch,
    setFilters,
    toggleContractSelection,
    selectAllContracts,
    clearSelection
} = invoiceSlice.actions

export default invoiceSlice.reducer
